import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from "@angular/common/http";
import { inject } from "@angular/core";
import { catchError, throwError } from "rxjs";
import { SnackbarService } from "../services/snackbar.service";

/**
 * Cuando se establece en true en el HttpContext de una petición, el interceptor
 * no muestra el toast de error. Útil para llamados "tolerados" cuyo error ya se
 * maneja en el servicio (por ejemplo chequeos de asociaciones en segundo plano).
 */
export const SKIP_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const snackbarService = inject(SnackbarService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const normalizedError: HttpErrorResponse = normalizeHttpError(error);
      let errorMessage = "Ha ocurrido un error inesperado";

      if (normalizedError.status >= 400) {
        switch (normalizedError.status) {
          case 400:
            errorMessage = getErrorMessage(normalizedError) || "Solicitud incorrecta";
            break;
          case 401:
            errorMessage =
              getFirstDefinedString(normalizedError.error, ["error", "message", "errortxt"]) ||
              "No autorizado. Por favor, inicie sesión nuevamente";
            break;
          case 403:
            errorMessage = getErrorMessage(normalizedError) || "No tiene permisos para realizar esta acción";
            break;
          case 404:
            errorMessage = getErrorMessage(normalizedError) || "Recurso no encontrado";
            break;
          case 409:
            errorMessage = getErrorMessage(normalizedError) || "Conflicto en la operación";
            break;
          case 422:
            errorMessage = getErrorMessage(normalizedError) || "Datos de entrada inválidos";
            break;
          case 500:
            errorMessage = getErrorMessage(normalizedError) || "Error interno del servidor";
            break;
          case 502:
            errorMessage = getErrorMessage(normalizedError) || "Servicio no disponible temporalmente";
            break;
          case 503:
            errorMessage = getErrorMessage(normalizedError) || "Servicio no disponible";
            break;
          case 555:
            errorMessage =
              getFirstDefinedString(normalizedError.error, ["cause", "message", "errortxt"]) ||
              "Servicio no disponible";
            break;
          default:
            errorMessage = getErrorMessage(normalizedError) || `Servicio no disponible Status: ${normalizedError.status}`;
        }

        if (!req.context.get(SKIP_ERROR_TOAST)) {
          snackbarService.error(errorMessage);
        }
      }

      return throwError(() => normalizedError);
    }),
  );
};

function normalizeHttpError(error: HttpErrorResponse): HttpErrorResponse {
  const normalizedMessage: string | null = extractBackendMessage(error);

  if (!normalizedMessage) {
    return error;
  }

  const normalizedPayload: Record<string, unknown> =
    error.error && typeof error.error === "object"
      ? { ...(error.error as Record<string, unknown>) }
      : {};

  if (!getFirstDefinedString(normalizedPayload, ["errortxt"])) {
    normalizedPayload["errortxt"] = normalizedMessage;
  }

  if (!getFirstDefinedString(normalizedPayload, ["message"])) {
    normalizedPayload["message"] = normalizedMessage;
  }

  return new HttpErrorResponse({
    error: normalizedPayload,
    headers: error.headers,
    status: error.status,
    statusText: error.statusText,
    url: error.url ?? undefined,
  });
}

function extractBackendMessage(error: HttpErrorResponse): string | null {
  return (
    getFirstDefinedString(error.error, ["errortxt", "message", "mensaje", "detail", "title", "error"]) ??
    getFirstDefinedString(error, ["message"])
  );
}

function getErrorMessage(error: HttpErrorResponse): string | null {
  return getFirstDefinedString(error.error, ["errortxt", "message", "mensaje", "detail", "title"]);
}

function getFirstDefinedString(source: unknown, keys: string[]): string | null {
  if (!source || typeof source !== "object") {
    return null;
  }

  for (const key of keys) {
    const value: unknown = (source as Record<string, unknown>)[key];

    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return null;
}
